/**
 * Machine — the fleet asset, its classification, and the condition bands that
 * colour every surface in the OEM+ port.
 *
 * Data Schema layer (layer 1 of the 4-layer architecture): type shapes and the
 * band constants that are their single source of truth. No behaviour beyond the
 * one pure lookup `bandOf()`, which is the literal port of the demo's `band()`.
 *
 * Ported from `OEM+_Platform_Demo.html`:
 *   `B.fleet`   — 100 machines (source line 1836, extracted to `core.json`)
 *   `B.classes` — 11 model definitions
 *   `BANDS` / `ATTN` / `BORDER` / `band()` / `STL` / `COL` — source line 1862–1867
 *
 * @see docs/schema/machine.md
 * @see docs/foundations/4-layer-architecture.md
 */

// ---------------------------------------------------------------------------
// Condition bands
// ---------------------------------------------------------------------------

/**
 * The five condition bands. A component's `health_score` (0–100) falls in
 * exactly one; a machine takes the band of its **worst** component.
 *
 * Demo comment, verbatim (source line 1861):
 * "condition / availability bands: >80 good · 60–80 fair · 40–60 poor ·
 *  20–40 very poor · <20 major issue"
 */
export type ConditionBand = 'good' | 'fair' | 'poor' | 'vpoor' | 'major';

/** One band's definition: its half-open score range, label and colour token. */
export interface ConditionBandSpec {
  /** The band key. */
  key: ConditionBand;
  /** Inclusive lower bound of `health_score`. */
  min: number;
  /**
   * Exclusive upper bound. `good` ends at 101, not 100, so a perfect score of
   * 100 still falls inside it — this is the demo's own bound, kept verbatim.
   */
  max: number;
  /** Display label. */
  label: string;
  /** CSS custom property carrying the band colour, defined in `oem.css`. */
  colorToken: string;
}

/**
 * The band table, in ascending score order — the demo's `BANDS` (source line
 * 1862). This is the single source of truth for thresholds; nothing else in the
 * port may hard-code 20 / 40 / 60 / 80.
 */
export const CONDITION_BANDS: readonly ConditionBandSpec[] = [
  { key: 'major', min: 0, max: 20, label: 'Major issue', colorToken: 'var(--major)' },
  { key: 'vpoor', min: 20, max: 40, label: 'Very poor', colorToken: 'var(--vpoor)' },
  { key: 'poor', min: 40, max: 60, label: 'Poor', colorToken: 'var(--poor)' },
  { key: 'fair', min: 60, max: 80, label: 'Fair', colorToken: 'var(--fair)' },
  { key: 'good', min: 80, max: 101, label: 'Good', colorToken: 'var(--good)' },
] as const;

/** Band key → display label. The demo's `STL` (source line 1867). */
export const CONDITION_BAND_LABEL: Readonly<Record<ConditionBand, string>> = {
  major: 'Major issue',
  vpoor: 'Very poor',
  poor: 'Poor',
  fair: 'Fair',
  good: 'Good',
};

/** Band key → CSS colour token. The band half of the demo's `COL` (line 1867). */
export const CONDITION_BAND_COLOR: Readonly<Record<ConditionBand, string>> = {
  major: 'var(--major)',
  vpoor: 'var(--vpoor)',
  poor: 'var(--poor)',
  fair: 'var(--fair)',
  good: 'var(--good)',
};

/**
 * Bands that mean "needs attention" — the demo's `ATTN` (source line 1863).
 * Drives the fleet attention counts, the watch list and predicted parts usage.
 */
export const ATTENTION_BANDS: readonly ConditionBand[] = ['major', 'vpoor', 'poor'] as const;

/**
 * Sort weight, worst first — the demo's `BORDER` / `BORD` (lines 1863, 2555).
 * Used wherever machines or components are ordered by severity.
 */
export const CONDITION_BAND_ORDER: Readonly<Record<ConditionBand, number>> = {
  major: 0,
  vpoor: 1,
  poor: 2,
  fair: 3,
  good: 4,
};

/**
 * Score → band. Literal port of the demo's `band()` (source line 1865).
 *
 * The fallback is `fair`, not `good` — the demo returns `BANDS[3]` when no
 * range matches, which happens for a negative score or one above 100. Kept as
 * written so ported figures match the original screen for screen.
 */
export function bandOf(score: number): ConditionBandSpec {
  return (
    CONDITION_BANDS.find((b) => score >= b.min && score < b.max) ??
    CONDITION_BANDS[3] /* fair */
  );
}

/** True when the band is one the fleet pages surface as needing attention. */
export const needsAttention = (band: ConditionBand): boolean => ATTENTION_BANDS.includes(band);

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------

/** Machine class key — the demo's `cls` field. */
export type MachineClass = 'jumbo' | 'longhole' | 'lhd' | 'truck' | 'sdrill';

/** Division key — the demo's `DIVNAME` keys (source line 1842). */
export type DivisionKey = 'drilling' | 'loadhaul' | 'surface';

/**
 * Division as stored on a machine record. Note `Load & Haul`: every screen
 * *reads* "Underground Load & Haul", but the data keeps the short form — see
 * the demo's `DIVLABEL` comment at source line 1844.
 */
export type DivisionName = 'Underground Drilling' | 'Load & Haul' | 'Surface Drilling';

/** Subclass as stored on a machine record — the demo's `SUBS` (line 1841). */
export type Subclass =
  | 'Development drills'
  | 'Production drills'
  | 'Loaders'
  | 'Haulage trucks'
  | 'Top-hammer drills'
  | 'Blasthole drills';

/** The eleven model codes in the fleet. */
export type ModelCode =
  | 'MJ-2D'
  | 'MP-60'
  | 'MP-60A'
  | 'ML-14'
  | 'ML-21'
  | 'ML-21E'
  | 'ML-21R'
  | 'MT-50'
  | 'MT-63'
  | 'MS-45'
  | 'MS-120';

/** Mine site code. */
export type MineCode = 'KG' | 'BR' | 'MI' | 'OH' | 'GD' | 'MR';

// ---------------------------------------------------------------------------
// Machine
// ---------------------------------------------------------------------------

/** One machine in the 100-strong fleet — a row of `core.json` `fleet`. */
export interface Machine {
  /** Stable id, e.g. `'L-02'`. Unique across the fleet; also the Maximo assetnum. */
  machine_id: string;
  /** Site the machine works at. */
  mine: MineCode;
  model: ModelCode;
  cls: MachineClass;
  division: DivisionName;
  subclass: Subclass;
  /** Free text, e.g. `'Diesel tram / electric drill'`, `'Battery-electric'`. */
  drive: string;
  /** Free text, e.g. `'Operator in cab'`, `'Tele-remote'`, `'Autonomous'`. */
  operating_mode: string;
  /** Free text size class, e.g. `'Large'`. */
  size: string;
  /** Manufacturer serial, e.g. `'MER-ML21-2019-948'`. */
  serial: string;
  /** ISO date, `YYYY-MM-DD`. */
  commissioned: string;
  engine_hours: number;
  remote: boolean;
  autonomous: boolean;
  battery: boolean;
  /** Customer or contractor operating the machine. */
  operated_by: string;
  list_price_aud: number;
  /** Named working within the mine, or `null` where the site has no subdivisions. */
  working: string | null;
}

/** A model definition — a value of `core.json` `classes`, keyed by `ModelCode`. */
export interface MachineClassDef {
  cls: MachineClass;
  division: DivisionName;
  subclass: Subclass;
  drive: string;
  /** Nominal diesel burn, litres per operating hour. */
  diesel_l_h: number;
  /** Nominal electrical draw, kWh per operating hour. */
  kwh_h: number;
  autonomy: string;
  /** Long name, e.g. `'MJ-2D twin-boom development jumbo'`. */
  name: string;
  size: string;
  /** How operating hours are counted, e.g. `'engine + percussion'`. */
  hour_basis: string;
  /** List price, AUD. */
  price: number;
  /** Component names used by the condition register for this model. */
  components: string[];
}

/**
 * Models with no side-view render of their own. The demo aliases them in
 * `TURN_MAP` (source line 1757); `public/machines/side/` ships nine files for
 * eleven models, so scenes must apply the same alias.
 */
export const SIDE_VIEW_ALIAS: Readonly<Partial<Record<ModelCode, ModelCode>>> = {
  'ML-14': 'ML-21',
  'MT-50': 'MT-63',
};

/** Resolve the side-view render for a model, following the alias. */
export const sideViewModel = (model: ModelCode): ModelCode => SIDE_VIEW_ALIAS[model] ?? model;
